import React, { useState, useEffect, useCallback } from "react";
import { IEvent, IShow } from "@eagerdog/interfaces";
import { Constants, UKC_JUNIOR_FEE_SHOW_TYPES } from "@eagerdog/constants";

import { helperService } from "src/services/helper.service";

import Input from "src/components/Input/Input";
import DatePick from "src/components/DatePick/DatePick";
import Checkbox, { ICheck } from "src/components/Checkbox/Checkbox";

import { INestedShowElement, getShowElementsByNested, shouldUseNestedSetup } from "../utils";

interface IProps {
  event?: IEvent,
  show?: IShow,
  currency?: string,
  showType: string,
  showElements: ICheck[],
  showElementsNested: INestedShowElement[],
  // C-WAGS FEO: when true a single flat "FEO Price" input is shown (applies to every FEO run)
  feoAvailable?: boolean,
  onChange(
    prices: IPrice[],
    useSamePrice: boolean,
    useEarlyBird: boolean,
    earlyBirdEndDate: any,
    juniorFee: string,
    earlyBirdJuniorFee: string,
    feoFee: string
  ): void
}

export interface IPrice {
  judge_element: string,
  judge_level?: string,
  judge_section?: string,
  fee: string,
  early_bird_fee?: string
}

const PriceTab: React.FC<IProps> = (props) => {
  const [useSamePrice, setUseSamePrice] = useState<boolean>(props.show ? props.show.use_same_fee : true);
  const [useEarlyBird, setUseEarlyBird] = useState<boolean>(props.show ? props.show.use_early_bird_fee : false);

  const isUKCConformation = useCallback(() => {
    return (
      props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
      props.showType === Constants.show_type.conformation
    );
  }, [props.event?.sanctioning_club, props.showType]);

  const isUKCConformationFreeClass = useCallback(
    (showElement?: string, level?: string) => {
      if (!isUKCConformation()) {
        return false;
      }

      const value = level || showElement || "";
      return (
        value === Constants.dog_class_element_level.total_dog ||
        value === Constants.dog_class_element_level.total_junior
      );
    },
    [isUKCConformation]
  );

  const checkForPreviousPrices = useCallback((statePrices?: IPrice[]) => {
    let _prices:IPrice[] = [];
    if (props.show) {
      if (useSamePrice) {
        let _price:IPrice = {
          judge_element: "",
          fee: props.show.fee.toString()
        };

        if (props.show.use_early_bird_fee && props.show.early_bird_fee) {
          _price.early_bird_fee = props.show.early_bird_fee.toString();
        }

        _prices.push(_price);

        return _prices;
      } else {
        props.show.show_elements.forEach((se, i) => {
          const isUKCFreeClass = isUKCConformationFreeClass(
            se.show_element,
            se.level
          );
          
          let _price: IPrice = {
            judge_element: se.level ? se.level : se.show_element,
            fee: isUKCFreeClass ? "0" : (se.fee?.toString() ?? statePrices?.[i]?.fee ?? ""),
          };

          if (shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')) {
            _price.judge_element = se.show_element;
            _price.judge_level = se.level;
            _price.judge_section = se.section;
          }

          if (se.early_bird_fee !== undefined) {
            _price.early_bird_fee = se.early_bird_fee.toString();
          }

          _prices.push(_price);
        })

        return _prices;
      }
    } else {
      let _price:IPrice = {
        judge_element: "",
        fee: "",
      };

      _prices.push(_price);

      return _prices;
    }
  }, [props.show, isUKCConformationFreeClass, useSamePrice]);

  const [prices, setPrices] = useState<IPrice[]>(checkForPreviousPrices());
  const [juniorFee, setJuniorFee] = useState<string>(props.show?.junior_fee?.toString() ?? "");
  const [earlyBirdJuniorFee, setEarlyBirdJuniorFee] = useState<string>(
    props.show?.early_bird_junior_fee?.toString() ?? ""
  );
  const [feoFee, setFeoFee] = useState<string>(props.show?.feo_fee?.toString() ?? "");

  // FEO rows (Obedience / Rally "FEO" section) are priced by the single FEO Price below,
  // never per level, so hide them from the per-class price lists.
  const isFeoPriceRow = (p: IPrice) => p.judge_section === Constants.dog_class_section.feo;

  const earlyDate = new Date();
  earlyDate.setMinutes(0);

  const [earlyBirdEndDate, setEarlyBirdEndDate] = useState<Date>(props.show?.early_bird_end_date ? new Date(props.show.early_bird_end_date) : earlyDate);

  const multiPrices:string[] = [
    Constants.show_type.conformation,
    Constants.show_type.scentwork,
    Constants.show_type.scent_detection,
    Constants.show_type.shed_dog,
    Constants.show_type.dock_jumping,
    Constants.show_type.obedience,
    Constants.show_type.rally_obedience,
    Constants.show_type.precision_coursing, 
    Constants.show_type.canine_good_citizen,
    Constants.show_type.trick_dog,
    Constants.show_type.herding,
    Constants.show_type.pre_trial_herding,
    Constants.show_type.instinct_test_herding,
    Constants.show_type.country_rat_race,
    Constants.show_type.barn_hunt,
    Constants.show_type.scent_dog_games,
  ];

  // UKC Agility's classes are a flat list of levels (Beginner A, Excellent,
  // etc.) - same shape as UKC Obedience/Rally - so it can use the same flat
  // per-checkbox pricing path. Scoped to UKC specifically (rather than
  // adding show_type.agility to multiPrices above) since AKC/4-H Agility use
  // a different, nested element/level structure that this flat path doesn't
  // support.
  const supportsMultiPricesUkcAgility =
    props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
    props.showType === Constants.show_type.agility;

  const supportsUkcJuniorFee = useCallback(() => {
    return (
      props.event?.sanctioning_club === Constants.sanctioning_club.UKC &&
      UKC_JUNIOR_FEE_SHOW_TYPES.includes(props.showType ?? "")
    );
  }, [props.event?.sanctioning_club, props.showType]);

  const updatePrice = (priceInfo: any, index: number) => {
    let _prices:IPrice[] = [...prices];
    _prices[index] = { ..._prices[index], ...priceInfo };

    setPrices(_prices);
  }

  const showElementIsChecked = (judge_element: string) => {
    let hasShowElement:ICheck[] = props.showElements.filter((s: ICheck) => { return s.label === judge_element && s.checked; });
    return !!hasShowElement.length;
  }

  const pUseSamePrice = helperService.usePrevious(useSamePrice);
  const pUseEarlyBird = helperService.usePrevious(useEarlyBird);
  const pShowElements = helperService.usePrevious(props.showElements);

  useEffect(() => {
    const hydratePrices = (elements?: any) => {
      let _prices:IPrice[] = [];

      if (elements) {
        if (shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')) {
          // init prices data from existing show_element records
          elements.forEach((el: IPrice) => {
            _prices.push({
              judge_element: el.judge_element,
              judge_level: el.judge_level,
              judge_section: el.judge_section,
              fee: el.fee,
              early_bird_fee: el.early_bird_fee,
            });
          });

        } else {

          for (let s in props.showElements) {
            let existingElement = elements.find((e: any) => {
              return (e.judge_element === props.showElements[s].label)
            });

            if (!existingElement) {
              if (props.showElements[s].checked) {
                const isUKCFreeClass = isUKCConformationFreeClass(
                  props.showElements[s].label
                );
                
                let p: IPrice = {
                  judge_element: props.showElements[s].label,
                  fee: isUKCFreeClass ? "0" : "",
                };

                _prices.push(p);
              }
            } else {
              let p: IPrice = {
                judge_element: existingElement.level ? existingElement.level : existingElement.judge_element,
                fee: existingElement.fee
              };

              if (existingElement.early_bird_fee !== undefined) {
                p.early_bird_fee = existingElement.early_bird_fee;
              }

              _prices.push(p);
            }
          }

        }
      } else {
        if (shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')) {
          const _elements = getShowElementsByNested(props.showElementsNested, undefined, prices);

          // init prices data for new show
          _elements.forEach((el) => {
            _prices.push({
              judge_element: el.show_element,
              judge_level: el.level,
              judge_section: el.section,
              fee: "",
              early_bird_fee: "",
            });
          });
        } else {
          for (let s in props.showElements) {
            const isUKCFreeClass = isUKCConformationFreeClass(
              props.showElements[s].label
            );
            
            _prices.push({ 
              judge_element: props.showElements[s].label, 
              fee: isUKCFreeClass ? "0" : "" 
            });
          }
        }
      }
      setPrices(_prices);
    }

    if ((pUseSamePrice !== useSamePrice) || (props.showElements !== pShowElements) || (useEarlyBird !== pUseEarlyBird)) {
      if (useSamePrice) {
        let p:IPrice = {
          judge_element: "",
          fee: props.show?.fee.toString() || prices[0].fee.toString() || "0"
        };

        if (props.show?.early_bird_fee) {
          p.early_bird_fee = props.show?.early_bird_fee.toString() || "";
        }

        setPrices([p]);
      } else {
        // When switching to multiple prices, rebuild from show elements
        let _prices: IPrice[] = [];
        const isNestedSetup = shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '');
        
        if (isNestedSetup && !props.show?.show_elements) {
          // For new nested shows, build prices from nested setup (element/level/section)
          const nestedElements = getShowElementsByNested(props.showElementsNested, undefined, prices);
          nestedElements.forEach((el) => {
            _prices.push({
              judge_element: el.show_element,
              judge_level: el.level,
              judge_section: el.section,
              fee: (el.fee?.toString() || prices[0]?.fee || "30"),
              early_bird_fee: el.early_bird_fee?.toString()
            });
          });
        } else if (props.show?.show_elements) {
          if (isNestedSetup) {
            // For edited nested shows, merge persisted prices with current selected nested elements.
            const selectedNestedElements = getShowElementsByNested(
              props.showElementsNested,
              undefined,
              prices
            );

            selectedNestedElements.forEach((selected) => {
              const existing = props.show?.show_elements?.find(
                (se: any) =>
                  se.show_element === selected.show_element &&
                  (se.level || "") === (selected.level || "") &&
                  (se.section || "") === (selected.section || "")
              );

              _prices.push({
                judge_element: selected.show_element,
                judge_level: selected.level,
                judge_section: selected.section,
                fee:
                  existing?.fee?.toString() ||
                  selected.fee?.toString() ||
                  prices[0]?.fee ||
                  "30",
                early_bird_fee:
                  existing?.early_bird_fee?.toString() ||
                  selected.early_bird_fee?.toString(),
              });
            });
          } else {
            // Build from existing saved elements first.
            props.show.show_elements.forEach((se: any) => {
              const isUKCFreeClass =
                isUKCConformationFreeClass(se.show_element, se.level);

              _prices.push({
                judge_element: se.level || se.show_element,
                fee: isUKCFreeClass ? "0" : (se.fee?.toString() || prices[0]?.fee || "30"),
                early_bird_fee: se.early_bird_fee?.toString(),
              });
            });

            // Add newly checked classes that do not yet exist in persisted show_elements.
            const existingLabels = new Set(_prices.map((p) => p.judge_element));
            props.showElements
              .filter((el: ICheck) => el.checked)
              .forEach((el: ICheck) => {
                if (existingLabels.has(el.label)) {
                  return;
                }

                const isUKCFreeClass =
                  isUKCConformationFreeClass(el.label);

                _prices.push({
                  judge_element: el.label,
                  fee: isUKCFreeClass ? "0" : prices[0]?.fee || "30",
                });
              });
          }
        } else {
          // For new shows, create prices based on checked elements
          props.showElements.filter((el: ICheck) => el.checked).forEach((el: ICheck) => {
            const isUKCFreeClass = isUKCConformationFreeClass(el.label);
            
            _prices.push({
              judge_element: el.label,
              fee: isUKCFreeClass ? "0" : (prices[0]?.fee || "30")
            });
          });
        }
        
        setPrices(_prices);
      }
    }
  }, [useSamePrice, pUseSamePrice, props.showElements, props.showElementsNested, pShowElements, checkForPreviousPrices, props.showType, props.show?.show_elements, props.show?.fee, useEarlyBird, pUseEarlyBird, prices, props.show?.early_bird_fee, isUKCConformationFreeClass]);

  let { onChange } = props;

  useEffect(() => {
    onChange(
      prices,
      useSamePrice,
      useEarlyBird,
      earlyBirdEndDate,
      juniorFee,
      earlyBirdJuniorFee,
      feoFee
    );
  }, [prices, useSamePrice, onChange, useEarlyBird, earlyBirdEndDate, juniorFee, earlyBirdJuniorFee, feoFee]);

  return (
    <div>
      <div className="stepTitle">Price</div>
      <p>What is the price of this show?</p>
      <>{(multiPrices.includes(props.showType) || supportsMultiPricesUkcAgility) && <div className="judgeCheckWrap"><Checkbox id="samePrice" label="Use the same price for all shows" value={useSamePrice} onChange={(e:any) => { setUseSamePrice(!useSamePrice); }} /></div>}</>
      {prices.map((p: IPrice, index: number) => {
        const isUKCFreeClass = isUKCConformationFreeClass(
          p.judge_element,
          p.judge_level
        );

        if (isUKCFreeClass || isFeoPriceRow(p)) {
          return <div key={index}></div>;
        }

        if (p.judge_element?.length === 0 || showElementIsChecked(p.judge_element) || shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')) {
          const label = useSamePrice
            ? ""
            : shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')
              ? [p.judge_element, p.judge_level, p.judge_section].filter(Boolean).join(" - ")
              : p.judge_element;

          return (
            <div key={"price" + index} className="inputwrap">
              <Input
                required
                type="text"
                placeholder={"What's the price of your " + label + " show?"}
                label={label + " Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
                defaultValue={p.fee}
                onChange={(e) => {
                  updatePrice({ fee: e.target.value }, index);
                }}
              />
            </div>
          );
        } else {
          return <div key={index}></div>;
        }
      })}
      {props.feoAvailable &&
        <Input
          required
          label={"FEO Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
          id="feoPrice"
          type="text"
          placeholder="What's the price of an FEO (For Exhibition Only) run? Applies to every FEO class."
          defaultValue={feoFee}
          onChange={(e) => {
            setFeoFee(e.target.value);
          }}
        />
      }
      {supportsUkcJuniorFee() &&
        <Input
          required
          label={"Junior Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
          id="juniorPrice"
          type="text"
          placeholder="What's the junior price?"
          defaultValue={juniorFee}
          onChange={(e) => {
            setJuniorFee(e.target.value);
          }}
        />
      }
      <Checkbox onChange={(e:any) => { setUseEarlyBird(!useEarlyBird); }} value={useEarlyBird} id="earlyBird" label="Use Early Bird Prices" />
      {useEarlyBird &&
        <div className="earlyBird">
          <p>What day do early bird prices end?</p>
          <DatePick label="Early Bird End Date" onChange={setEarlyBirdEndDate} value={earlyBirdEndDate} showTimeSelect={true} />
          {useSamePrice &&
            <Input
              required
              label={"Early Bird Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
              id="earlyBirdPrice" type="text"
              placeholder="What's the early bird price?"
              defaultValue={prices[0].early_bird_fee || ""}
              onChange={(e) => {
                e.stopPropagation();
                updatePrice({ early_bird_fee: e.target.value }, 0);
              }}
            />
          }
          <>{!useSamePrice && <div className="nonLicensedPrices">
            {prices.map((p: IPrice, index:number) => {
              const isUKCFreeClass = isUKCConformationFreeClass(
                p.judge_element,
                p.judge_level
              );

              if (isUKCFreeClass || isFeoPriceRow(p)) {
                return <div key={index}></div>;
              }

              if (showElementIsChecked(p.judge_element) || shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')) {
                const earlyBirdLabel = shouldUseNestedSetup(props.showType, props.event?.sanctioning_club || '')
                  ? [p.judge_element, p.judge_level, p.judge_section].filter(Boolean).join(" - ")
                  : p.judge_element;
                return(
                  <div key={"price" + index} className="inputwrap">
                    <Input
                      type="text"
                      label={earlyBirdLabel + " Early Bird Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
                      placeholder={"What's the early bird price of your "+ earlyBirdLabel +" show?"}
                      defaultValue={p.early_bird_fee}
                      onChange={(e) => {
                        updatePrice({ early_bird_fee: e.target.value }, index);
                      }}
                    />
                  </div>
                );
              } else {
                return <div key={index}></div>;
              }
            })}
          </div>}</>
          {supportsUkcJuniorFee() && (
            <Input
              required
              label={"Early Bird Junior Price (" + (props.currency?.toUpperCase() || "USD") + ")"}
              id="earlyBirdJuniorPrice"
              type="text"
              placeholder="What's the early bird junior price?"
              defaultValue={earlyBirdJuniorFee}
              onChange={(e) => {
                setEarlyBirdJuniorFee(e.target.value);
              }}
            />
          )}
        </div>
      }
    </div>
  );
};

export default PriceTab;